import {
  Input,
  Slider,
  Stack,
  Text,
  VStack,
  createListCollection,
} from "@chakra-ui/react";
import * as React from "react";

import {
  ConversionActionPlatform,
  ConversionActionTarget,
  CreateConversionActionRequest,
  createConversionAction,
} from "../api/conversion-action";
import {
  GoogleAdsConversionAction,
  GoogleAdsCustomer,
  getGoogleAdsConversionActions,
  getGoogleAdsCustomers,
} from "../api/google-ads";
import { validateFacebookCapiToken } from "../api/integration";
import {
  useAppSelector,
  useLoading,
  useToaster,
  useWorkspaceURL,
} from "../hooks";
import { brand } from "../lib/brand";
import { formatStatus } from "../lib/format";
import { IntegrationStatus } from "../types/integration";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { ChoiceCard } from "./choice-card";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import {
  FacebookAdAccountField,
  FacebookNotConnectedMessage,
} from "./facebook-ad-account-field";
import { FacebookCapiCredentialsFields } from "./facebook-capi-credentials-fields";
import { Field } from "./field";
import Link from "./link";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "./select";
import { toaster } from "./toaster";

type ModalStep = "account" | "conversion-action" | "target";

interface CreateConversionActionDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  platform: ConversionActionPlatform;
  workspaceSlug: string;
}

export function CreateConversionActionDialog({
  isOpen,
  onOpenChange,
  onSuccess,
  platform,
  workspaceSlug,
}: CreateConversionActionDialogProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [currentStep, setCurrentStep] = React.useState<ModalStep>("account");

  const [customers, setCustomers] = React.useState<GoogleAdsCustomer[]>([]);
  const [conversionActions, setConversionActions] = React.useState<
    GoogleAdsConversionAction[]
  >([]);
  const [selectedCustomer, setSelectedCustomer] = React.useState<string>("");
  const [selectedConversionAction, setSelectedConversionAction] =
    React.useState<string>("");
  const [selectedTarget, setSelectedTarget] = React.useState<
    ConversionActionTarget | ""
  >("");
  const [binaryPercentile, setBinaryPercentile] = React.useState<number>(25);
  const [binaryBelow, setBinaryBelow] = React.useState<boolean>(false);

  const [selectedAdAccount, setSelectedAdAccount] = React.useState<string>("");
  const [metaConversionActionName, setMetaConversionActionName] =
    React.useState<string>("");
  const [selectedDataset, setSelectedDataset] = React.useState<string>("");
  const [accessToken, setAccessToken] = React.useState<string>("");

  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();
  const integrations = useAppSelector((state) => state.workspace.integrations);
  const workspaceURL = useWorkspaceURL();

  const isGoogle = platform === ConversionActionPlatform.GOOGLE_ADS;
  const isMeta = platform === ConversionActionPlatform.META;
  const selectedLoginCustomerId =
    customers.find(({ id }) => id === selectedCustomer)?.[
      "login-customer-id"
    ] ?? null;

  // Every addie conversion event is binary, so there's no strategy to pick.
  const choosesTarget = brand.id !== "addie";
  const target = choosesTarget ? selectedTarget : ConversionActionTarget.BINARY;
  const lastStep: ModalStep = choosesTarget ? "target" : "conversion-action";

  const integrationId = isGoogle ? "google-ads" : "facebook";
  const isConnected = React.useMemo(() => {
    return integrations.some(
      (i) => i.id === integrationId && i.status === IntegrationStatus.connected,
    );
  }, [integrationId, integrations]);

  const customersCollection = React.useMemo(() => {
    return createListCollection({
      items: customers.map((customer) => ({
        value: customer.id,
        label: customer.name,
      })),
    });
  }, [customers]);

  const conversionActionsCollection = React.useMemo(() => {
    return createListCollection({
      items: conversionActions.map((action) => ({
        value: action.id,
        label: action.name,
      })),
    });
  }, [conversionActions]);

  const resetState = React.useCallback(() => {
    setCurrentStep("account");
    setSelectedCustomer("");
    setSelectedConversionAction("");
    setSelectedTarget("");
    setBinaryPercentile(25);
    setBinaryBelow(false);
    setCustomers([]);
    setConversionActions([]);
    setSelectedAdAccount("");
    setMetaConversionActionName("");
    setSelectedDataset("");
    setAccessToken("");
  }, []);

  const handleOpenChange = React.useCallback(
    (details: { open: boolean }) => {
      if (!details.open) {
        resetState();
      }
      onOpenChange(details);
    },
    [onOpenChange, resetState],
  );

  React.useEffect(() => {
    if (currentStep !== "account" || !isOpen || !isGoogle) return;
    if (customers.length > 0) return;
    withLoading(async () => {
      await withToaster(async () => {
        const data = await getGoogleAdsCustomers(workspaceSlug);
        setCustomers(data);
      });
    });
  }, [
    currentStep,
    customers.length,
    isGoogle,
    isOpen,
    withLoading,
    withToaster,
    workspaceSlug,
  ]);

  React.useEffect(() => {
    if (
      currentStep !== "conversion-action" ||
      !selectedCustomer ||
      !isOpen ||
      !isGoogle
    )
      return;
    if (conversionActions.length > 0) return;
    withLoading(async () => {
      await withToaster(async () => {
        const data = await getGoogleAdsConversionActions(
          workspaceSlug,
          selectedCustomer,
          selectedLoginCustomerId,
        );
        setConversionActions(data);
      });
    });
  }, [
    conversionActions.length,
    currentStep,
    isGoogle,
    isOpen,
    selectedCustomer,
    selectedLoginCustomerId,
    withLoading,
    withToaster,
    workspaceSlug,
  ]);

  const validateCapiToken = () =>
    validateFacebookCapiToken(
      workspaceSlug,
      selectedDataset,
      accessToken.trim(),
    );

  const handleNext = async () => {
    if (currentStep === "account") {
      setCurrentStep("conversion-action");
    } else if (currentStep === "conversion-action") {
      if (isMeta) {
        await withLoading(async () => {
          await withToaster(async () => {
            await validateCapiToken();
            setCurrentStep("target");
          });
        });
      } else {
        setCurrentStep("target");
      }
    }
  };

  const handleBack = () => {
    if (currentStep === "target") {
      setCurrentStep("conversion-action");
      setSelectedTarget("");
      setBinaryPercentile(25);
      setBinaryBelow(false);
    } else if (currentStep === "conversion-action") {
      setCurrentStep("account");
      if (isGoogle) {
        setConversionActions([]);
        setSelectedConversionAction("");
      } else {
        setMetaConversionActionName("");
        setSelectedDataset("");
        setAccessToken("");
      }
    }
  };

  const canProceed = (): boolean => {
    if (!isConnected) return false;
    if (currentStep === "account") {
      return isGoogle ? selectedCustomer !== "" : selectedAdAccount !== "";
    }
    if (currentStep === "conversion-action") {
      if (isGoogle) {
        return selectedConversionAction !== "";
      }
      return (
        metaConversionActionName.trim() !== "" &&
        selectedDataset !== "" &&
        accessToken.trim() !== ""
      );
    }
    return false;
  };

  const canCreate = (): boolean => {
    if (isGoogle) {
      return (
        selectedCustomer !== "" &&
        selectedConversionAction !== "" &&
        target !== ""
      );
    }
    return (
      selectedAdAccount !== "" &&
      metaConversionActionName.trim() !== "" &&
      selectedDataset !== "" &&
      accessToken.trim() !== "" &&
      target !== ""
    );
  };

  const handleCreate = async () => {
    await withLoading(async () => {
      // Without a strategy step, nothing else has checked the token yet.
      if (isMeta && !choosesTarget) {
        await withToaster(validateCapiToken);
      }
      try {
        await withToaster(async () => {
          let requestData: CreateConversionActionRequest;

          if (isGoogle) {
            const selectedAction = conversionActions.find(
              (a) => a.id === selectedConversionAction,
            );
            if (!selectedAction) {
              throw new Error("Conversion event not found");
            }
            requestData = {
              platform: ConversionActionPlatform.GOOGLE_ADS,
              name: selectedAction.name,
              target: target as ConversionActionTarget,
              "binary-percentile": binaryPercentile,
              "binary-below?": binaryBelow,
              "google-ads-account": selectedCustomer,
              "google-ads-login-customer-id": selectedLoginCustomerId,
              "google-ads-id": selectedConversionAction,
              "facebook-ad-account": null,
              "dataset-id": null,
              "access-token": null,
            };
          } else {
            requestData = {
              platform: ConversionActionPlatform.META,
              name: metaConversionActionName.trim(),
              target: target as ConversionActionTarget,
              "binary-percentile": binaryPercentile,
              "binary-below?": binaryBelow,
              "google-ads-account": null,
              "google-ads-login-customer-id": null,
              "google-ads-id": null,
              "facebook-ad-account": selectedAdAccount,
              "dataset-id": selectedDataset,
              "access-token": accessToken.trim(),
            };
          }

          await createConversionAction(workspaceSlug, requestData);

          toaster.create({
            type: "success",
            title: "Conversion event created",
            description: `${requestData.name} has been created successfully.`,
          });

          handleOpenChange({ open: false });
          onSuccess();
        });
      } catch (error) {
        const axiosError = error as {
          response?: { data?: { error?: { message?: string } } };
          message?: string;
        };
        toaster.create({
          type: "error",
          title: "Failed to create conversion event",
          description:
            axiosError?.response?.data?.error?.message ||
            axiosError?.message ||
            "An error occurred while creating the conversion event.",
        });
      }
    });
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case "account":
        return isGoogle
          ? "Select Google Ads Account"
          : "Select Meta Ad Account";
      case "conversion-action":
        return isGoogle
          ? "Select Conversion Event"
          : "Conversion Event Details";
      case "target":
        return "Select Bid Strategy";
      default:
        return "Create Conversion Event";
    }
  };

  const renderAccountStep = () => {
    if (!isConnected) {
      if (isMeta) {
        return (
          <FacebookNotConnectedMessage purpose="create conversion events" />
        );
      }
      return (
        <Text fontSize="sm" color="fg.muted">
          Please{" "}
          <Link
            to={workspaceURL("settings/integrations")}
            textDecoration="underline"
          >
            connect your Google Ads account
          </Link>{" "}
          to create conversion events.
        </Text>
      );
    }

    if (isGoogle) {
      return (
        <Field label="Google Ads Account" required>
          {isLoading && customers.length === 0 ? (
            <Text fontSize="sm" color="fg.muted">
              Loading Google Ads accounts...
            </Text>
          ) : customers.length === 0 ? (
            <Text fontSize="sm" color="fg.muted">
              No Google Ads accounts found. Please connect your Google Ads
              account in Settings.
            </Text>
          ) : (
            <SelectRoot
              size="sm"
              value={selectedCustomer ? [selectedCustomer] : []}
              onValueChange={({ value }) => setSelectedCustomer(value[0])}
              collection={customersCollection}
            >
              <SelectTrigger>
                <SelectValueText placeholder="Select an account" />
              </SelectTrigger>
              <SelectContent portalRef={ref as React.RefObject<HTMLElement>}>
                {customersCollection.items.map((item) => (
                  <SelectItem key={item.value} item={item}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          )}
        </Field>
      );
    }

    return (
      <FacebookAdAccountField
        workspaceSlug={workspaceSlug}
        value={selectedAdAccount}
        onChange={setSelectedAdAccount}
        portalRef={ref as React.RefObject<HTMLElement>}
        label="Meta Ad Account"
      />
    );
  };

  const renderConversionActionStep = () => {
    if (isGoogle) {
      return (
        <Field label="Conversion Event" required>
          {isLoading && conversionActions.length === 0 ? (
            <Text fontSize="sm" color="fg.muted">
              Loading conversion events...
            </Text>
          ) : conversionActions.length === 0 ? (
            <Text fontSize="sm" color="fg.muted">
              No conversion events found for this account.
            </Text>
          ) : (
            <SelectRoot
              size="sm"
              value={selectedConversionAction ? [selectedConversionAction] : []}
              onValueChange={({ value }) => {
                if (value && value[0]) {
                  setSelectedConversionAction(value[0]);
                }
              }}
              collection={conversionActionsCollection}
            >
              <SelectTrigger>
                <SelectValueText placeholder="Select a conversion event" />
              </SelectTrigger>
              <SelectContent portalRef={ref as React.RefObject<HTMLElement>}>
                {conversionActionsCollection.items.map((item) => {
                  const action = conversionActions.find(
                    (a) => a.id === item.value,
                  );
                  return (
                    <SelectItem key={item.value} item={item}>
                      <VStack align="start" gap="0">
                        <Text>{item.label}</Text>
                        {action && (
                          <Text fontSize="xs" color="fg.muted">
                            {formatStatus(String(action.category))} •{" "}
                            {formatStatus(String(action.status))}
                          </Text>
                        )}
                      </VStack>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </SelectRoot>
          )}
        </Field>
      );
    }

    return (
      <Stack gap="4">
        <Field label="Conversion Event Name" required>
          <Input
            size="sm"
            placeholder="Enter a name for this conversion event"
            value={metaConversionActionName}
            onChange={(e) => setMetaConversionActionName(e.target.value)}
          />
        </Field>
        <FacebookCapiCredentialsFields
          workspaceSlug={workspaceSlug}
          adAccountId={selectedAdAccount}
          dataset={selectedDataset}
          onDatasetChange={setSelectedDataset}
          accessToken={accessToken}
          onAccessTokenChange={setAccessToken}
          portalRef={ref as React.RefObject<HTMLElement>}
          datasetHelperText="The dataset where conversion events are sent."
        />
      </Stack>
    );
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange} size="md">
      <DialogContent maxWidth={"560px" as any} ref={ref}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>{getStepTitle()}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap="4">
            {currentStep === "account" && renderAccountStep()}
            {currentStep === "conversion-action" &&
              renderConversionActionStep()}
            {currentStep === "target" && (
              <Field label="Bid Strategy">
                <Stack gap="2.5" width="full">
                  <ChoiceCard
                    title="Value"
                    description="Sends each shopper's predicted 12-month LTV as the conversion value. The platform's bidder optimizes spend toward your highest-LTV shoppers."
                    selected={selectedTarget === ConversionActionTarget.VALUE}
                    onSelect={() =>
                      setSelectedTarget(ConversionActionTarget.VALUE)
                    }
                  />
                  <ChoiceCard
                    title="Scaled"
                    description="Same as Value, but rescales predicted LTVs to match your brand's historical order-value distribution. Preserves ranking; gives the bidder numbers in a familiar range."
                    selected={selectedTarget === ConversionActionTarget.SCALED}
                    onSelect={() =>
                      setSelectedTarget(ConversionActionTarget.SCALED)
                    }
                  />
                  <ChoiceCard
                    title="Binary"
                    description="Syncs only shoppers whose predicted LTV crosses the chosen percentile threshold, sending it as the conversion value. The platform optimizes for finding more like them."
                    selected={selectedTarget === ConversionActionTarget.BINARY}
                    onSelect={() =>
                      setSelectedTarget(ConversionActionTarget.BINARY)
                    }
                  >
                    {selectedTarget === ConversionActionTarget.BINARY && (
                      <Stack
                        gap="3"
                        marginTop="3"
                        onClick={(e) => e.stopPropagation()}
                        css={{ cursor: "default" }}
                      >
                        <Field label={`Threshold: P${binaryPercentile}`}>
                          <Slider.Root
                            width="full"
                            min={1}
                            max={99}
                            step={1}
                            value={[binaryPercentile]}
                            onValueChange={({ value }) =>
                              setBinaryPercentile(value[0])
                            }
                          >
                            <Slider.Control>
                              <Slider.Track>
                                <Slider.Range />
                              </Slider.Track>
                              <Slider.Thumb index={0}>
                                <Slider.HiddenInput />
                              </Slider.Thumb>
                            </Slider.Control>
                          </Slider.Root>
                        </Field>
                        <Checkbox
                          checked={binaryBelow}
                          onCheckedChange={({ checked }) =>
                            setBinaryBelow(checked === true)
                          }
                        >
                          <Text fontSize="sm">
                            Fire when predicted value is below the threshold
                          </Text>
                        </Checkbox>
                      </Stack>
                    )}
                  </ChoiceCard>
                </Stack>
              </Field>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="surface"
            onClick={() => handleOpenChange({ open: false })}
          >
            Cancel
          </Button>
          {currentStep !== "account" && (
            <Button variant="surface" onClick={handleBack}>
              Back
            </Button>
          )}
          {currentStep !== lastStep ? (
            <Button
              variant="solid"
              onClick={handleNext}
              disabled={!canProceed()}
              loading={isLoading}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="solid"
              onClick={handleCreate}
              loading={isLoading}
              disabled={!canCreate()}
            >
              Create
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
